import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Trash2, BarChart3, Mail, Loader2, FileText, Copy, X, Bookmark } from 'lucide-react';
import { Toaster, toast } from 'sonner';
import {
  listCampaigns, createCampaign, deleteCampaign,
  duplicateCampaign,
  listCampaignTemplateLibrary, getCampaignTemplate, deleteCampaignTemplate,
} from '../../lib/campaignsApi';

const formatDate = (iso) => {
  if (!iso) return '\u2014';
  return new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const StatusBadge = ({ status }) => {
  const map = {
    draft: 'bg-white/10 text-white/70',
    sending: 'bg-amber-500/15 text-amber-400 border border-amber-400/30',
    sent: 'bg-emerald-500/15 text-emerald-400 border border-emerald-400/30',
    failed: 'bg-red-500/15 text-red-400 border border-red-400/30',
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${map[status] || map.draft}`}>
      {status}
    </span>
  );
};

export const AdminCampaigns = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [duplicatingId, setDuplicatingId] = useState(null);

  // Template library picker
  const [tplOpen, setTplOpen] = useState(false);
  const [tplLoading, setTplLoading] = useState(false);
  const [tplList, setTplList] = useState([]);
  const [tplCategories, setTplCategories] = useState([]);
  const [tplActiveCategory, setTplActiveCategory] = useState('All');
  const [tplApplyingId, setTplApplyingId] = useState(null);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const r = await listCampaigns();
      setCampaigns(r.data);
    } catch (e) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const handleCreate = async () => {
    try {
      setCreating(true);
      // Feb-2026 — New Campaign must be COMPLETELY blank. Root cause
      // for pre-filled drafts was `template_key: 'weekly_inventory_update'`
      // sent here, which the backend's `create_campaign` route
      // expanded into the weekly template's subject / preheader /
      // body_html. Do NOT pass template_key so nothing preloads.
      // "From Template" (below) is the ONLY path that seeds content.
      const r = await createCampaign({
        name: `Untitled Campaign — ${new Date().toLocaleDateString()}`,
        subject: '',
        preheader: '',
        body_html: '',
        template_key: null,
      });
      navigate(`/admin/campaigns/${r.data.id}`);
    } catch (e) {
      toast.error('Failed to create campaign');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete draft "${name}"? This cannot be undone.`)) return;
    try {
      await deleteCampaign(id);
      toast.success('Campaign deleted');
      fetchCampaigns();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleDuplicate = async (id, name) => {
    try {
      setDuplicatingId(id);
      const r = await duplicateCampaign(id);
      toast.success(`Duplicated "${name}"`);
      navigate(`/admin/campaigns/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to duplicate');
    } finally {
      setDuplicatingId(null);
    }
  };

  // ─── Template Library ─────────────────────────────────────────────
  const openTemplateLibrary = async () => {
    setTplOpen(true);
    try {
      setTplLoading(true);
      const r = await listCampaignTemplateLibrary();
      setTplList(r.data.templates || []);
      setTplCategories(['All', ...(r.data.categories || [])]);
      setTplActiveCategory('All');
    } catch (e) {
      toast.error('Failed to load templates');
    } finally {
      setTplLoading(false);
    }
  };

  const filteredTemplates = useMemo(() => {
    if (tplActiveCategory === 'All') return tplList;
    return tplList.filter((t) => t.category === tplActiveCategory);
  }, [tplList, tplActiveCategory]);

  const applyTemplate = async (tpl) => {
    try {
      setTplApplyingId(tpl.id);
      // Hydrate body from the detail endpoint (list view omits html_body).
      const full = await getCampaignTemplate(tpl.id);
      const created = await createCampaign({
        name: `${tpl.name} — ${new Date().toLocaleDateString()}`,
        subject: full.data.subject || '',
        preheader: full.data.preheader || '',
        body_html: full.data.html_body || '',
        template_key: full.data.template_key || null,
      });
      toast.success('Campaign created from template');
      setTplOpen(false);
      navigate(`/admin/campaigns/${created.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create from template');
    } finally {
      setTplApplyingId(null);
    }
  };

  const removeTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await deleteCampaignTemplate(tpl.id);
      toast.success('Template deleted');
      setTplList((prev) => prev.filter((t) => t.id !== tpl.id));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete template');
    }
  };

  return (
    <div className="p-6" data-testid="admin-campaigns-page">
      <Toaster position="top-right" theme="dark" />

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white mb-1" data-testid="campaigns-title">Email Campaigns</h1>
          <p className="text-white/55 text-sm">Send wholesale inventory updates to your subscriber list.</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/admin/email-analytics"
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="open-email-analytics-btn"
          >
            <BarChart3 className="w-4 h-4" /> Email Analytics
          </Link>
          <button
            onClick={openTemplateLibrary}
            className="btn-secondary inline-flex items-center gap-2"
            data-testid="from-template-btn"
          >
            <FileText className="w-4 h-4" /> From Template
          </button>
          <button
            onClick={handleCreate}
            disabled={creating}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="new-campaign-btn"
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            New Campaign
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-white/50 text-sm">Loading…</p>
      ) : campaigns.length === 0 ? (
        <div className="border border-white/10 bg-[#0A0A0A] p-10 text-center" data-testid="campaigns-empty">
          <Mail className="w-10 h-10 mx-auto text-white/20 mb-3" />
          <p className="text-white/55 text-sm mb-4">No campaigns yet.</p>
          <button onClick={handleCreate} className="btn-primary inline-flex items-center gap-2">
            <Plus className="w-4 h-4" /> Create your first campaign
          </button>
        </div>
      ) : (
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead className="bg-[#0A0A0A] text-white/55 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Campaign</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Recipients</th>
                <th className="text-left px-4 py-3">Sent</th>
                <th className="text-left px-4 py-3" title="Unique people who opened / total open events">Opens <span className="text-white/30 normal-case text-[10px]">(people / events)</span></th>
                <th className="text-left px-4 py-3" title="Unique people who clicked / total click events">Clicks <span className="text-white/30 normal-case text-[10px]">(people / events)</span></th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`campaign-row-${c.id}`}>
                  <td className="px-4 py-3">
                    <Link to={`/admin/campaigns/${c.id}`} className="text-white hover:text-[#00D4FF] font-medium" data-testid={`campaign-link-${c.id}`}>
                      {c.name}
                    </Link>
                    <div className="text-xs text-white/40 truncate max-w-[320px]">{c.subject || <em className="text-white/30">No subject yet</em>}</div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                  <td className="px-4 py-3 text-white/70">{c.recipient_count || '\u2014'}</td>
                  <td className="px-4 py-3 text-white/70">{c.sent_count || 0}</td>
                  <td className="px-4 py-3 text-white/70" title={`${c.unique_open_count || 0} people opened · ${c.open_count || 0} total open events`}>{c.unique_open_count || 0} <span className="text-white/30 text-xs">/ {c.open_count || 0}</span></td>
                  <td className="px-4 py-3 text-white/70" title={`${c.unique_click_count || 0} people clicked · ${c.click_count || 0} total click events`}>{c.unique_click_count || 0} <span className="text-white/30 text-xs">/ {c.click_count || 0}</span></td>
                  <td className="px-4 py-3 text-white/50 text-xs">{formatDate(c.created_at)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-3 justify-end">
                      <button
                        onClick={() => handleDuplicate(c.id, c.name)}
                        disabled={duplicatingId === c.id}
                        className="text-white/40 hover:text-[#00D4FF] transition-colors disabled:opacity-30"
                        data-testid={`duplicate-campaign-${c.id}`}
                        title="Duplicate campaign"
                      >
                        {duplicatingId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Copy className="w-4 h-4" />}
                      </button>
                      {(c.status === 'draft' || c.status === 'failed') && (
                        <button
                          onClick={() => handleDelete(c.id, c.name)}
                          className="text-white/40 hover:text-red-400 transition-colors"
                          data-testid={`delete-campaign-${c.id}`}
                          title="Delete draft"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Template Library modal */}
      {tplOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setTplOpen(false)}
          data-testid="template-library-modal"
        >
          <div className="relative bg-[#0A0A0A] border border-white/10 w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <div className="flex items-center gap-2">
                <Bookmark className="w-4 h-4 text-[#00D4FF]" />
                <div className="text-sm font-semibold text-white">Choose a template</div>
              </div>
              <button onClick={() => setTplOpen(false)} className="text-white/40 hover:text-white" data-testid="close-template-library">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Category tabs */}
            <div className="flex gap-1 px-4 pt-3 flex-wrap">
              {tplCategories.map((cat) => {
                const count = cat === 'All' ? tplList.length : tplList.filter((t) => t.category === cat).length;
                return (
                  <button
                    key={cat}
                    onClick={() => setTplActiveCategory(cat)}
                    className={`px-3 py-1.5 text-xs border transition-colors ${
                      tplActiveCategory === cat
                        ? 'bg-[#00D4FF] text-black border-[#00D4FF]'
                        : 'border-white/15 text-white/70 hover:border-white/30 hover:text-white'
                    }`}
                    data-testid={`tpl-category-${cat.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {cat} <span className="opacity-60">({count})</span>
                  </button>
                );
              })}
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              {tplLoading ? (
                <div className="text-white/50 text-sm">Loading templates…</div>
              ) : filteredTemplates.length === 0 ? (
                <div className="text-center py-10 text-white/40 text-sm">
                  No templates in this category yet.
                  <div className="text-xs text-white/30 mt-1">Open any campaign and click <span className="text-white/60">&quot;Save as Template&quot;</span> to add one.</div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredTemplates.map((tpl) => (
                    <div key={tpl.id} className="border border-white/10 bg-[#050505] p-3 hover:border-[#00D4FF]/40 transition-colors" data-testid={`tpl-card-${tpl.id}`}>
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <div className="text-white text-sm font-medium leading-tight">{tpl.name}</div>
                        {tpl.is_system && (
                          <span className="text-[9px] uppercase tracking-wider text-[#00D4FF]/80 border border-[#00D4FF]/30 px-1.5 py-0.5">System</span>
                        )}
                      </div>
                      <div className="text-[11px] text-white/40 mb-2">{tpl.category}</div>
                      <div className="text-xs text-white/60 line-clamp-2 mb-3">{tpl.subject || <em className="text-white/30">No subject saved</em>}</div>
                      <div className="flex gap-2 justify-between items-center">
                        <button
                          onClick={() => applyTemplate(tpl)}
                          disabled={tplApplyingId === tpl.id}
                          className="btn-primary text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                          data-testid={`tpl-use-${tpl.id}`}
                        >
                          {tplApplyingId === tpl.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                          Use Template
                        </button>
                        {!tpl.is_system && (
                          <button
                            onClick={() => removeTemplate(tpl)}
                            className="text-white/30 hover:text-red-400 transition-colors"
                            data-testid={`tpl-delete-${tpl.id}`}
                            title="Delete template"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
